import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useCreateVendor,
  useUpdateVendor,
  useVendors,
  VENDOR_TYPES,
  VENDOR_TYPE_LABELS,
  type CreateVendorInput,
  type Vendor,
} from '@/features/procurement/hooks';
import { findVendorNameMatches, vendorNameKey } from '@/features/procurement/vendorNames';
import { formatError } from '@/lib/errors';
import type { VendorType } from '@/types/database';

interface NewSupplierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When provided the dialog becomes an EDIT form pre-filled with this
   * vendor's data; submitting updates the row (and marks it "Pending
   * push" for QuickBooks). When omitted it's the original create form.
   */
  vendor?: Vendor | null;
  /** Called with the new vendor's id once the row has been created. */
  onCreated?: (vendorId: string) => void;
  /**
   * When provided, duplicate / similar-name warnings get a "Use this
   * vendor" action that hands the existing row back to the caller
   * (e.g. the PO form selects it) instead of creating a new one.
   */
  onUseExisting?: (vendor: Vendor) => void;
  /** Pre-fills the name field when creating. */
  initialName?: string;
  /** Pre-fills contact / address fields when creating (e.g. read off an order PDF). */
  initialValues?: CreateVendorInput | null;
}

// Sentinel for the type <Select> — Radix doesn't allow an empty-string
// item value, so "no type chosen" is its own token.
const TYPE_NONE = 'none';

/**
 * Create / edit modal for the vendor master. Covers contact basics,
 * the supplier category, billing address, what the vendor supplies,
 * and who owns the relationship internally.
 */
export function NewSupplierDialog({
  open,
  onOpenChange,
  vendor,
  onCreated,
  onUseExisting,
  initialName,
  initialValues,
}: NewSupplierDialogProps) {
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();
  const { data: vendors } = useVendors();
  const isEdit = !!vendor;

  const [name, setName] = useState('');
  const [vendorType, setVendorType] = useState<string>(TYPE_NONE);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactOwner, setContactOwner] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [supplies, setSupplies] = useState('');
  const [notes, setNotes] = useState('');

  // On open: seed from the vendor being edited, or reset to a blank
  // draft so a previous form state doesn't bleed through.
  useEffect(() => {
    if (!open) return;
    const seed = vendor ? null : initialValues;
    setName(vendor?.name ?? initialName ?? seed?.name ?? '');
    setVendorType(vendor?.vendor_type ?? seed?.vendor_type ?? TYPE_NONE);
    setEmail(vendor?.email ?? seed?.email ?? '');
    setPhone(vendor?.phone ?? seed?.phone ?? '');
    setWebsite(vendor?.website ?? seed?.website ?? '');
    setContactName(vendor?.contact_name ?? seed?.contact_name ?? '');
    setContactOwner(vendor?.contact_owner ?? seed?.contact_owner ?? '');
    setAddressLine1(vendor?.address_line1 ?? seed?.address_line1 ?? '');
    setAddressLine2(vendor?.address_line2 ?? seed?.address_line2 ?? '');
    setCity(vendor?.city ?? seed?.city ?? '');
    setState(vendor?.state ?? seed?.state ?? '');
    setZipCode(vendor?.zip_code ?? seed?.zip_code ?? '');
    setSupplies(vendor?.supplies ?? seed?.supplies ?? '');
    setNotes(vendor?.notes ?? seed?.notes ?? '');
  }, [open, vendor, initialName, initialValues]);

  // Renaming an existing vendor to the same key (e.g. fixing case) is
  // never a duplicate — mirrors the DB trigger, which only checks when
  // the normalised name changes.
  const nameChanged = !isEdit || vendorNameKey(name) !== vendorNameKey(vendor?.name ?? '');
  const matches = useMemo(
    () =>
      nameChanged
        ? findVendorNameMatches(name, vendors ?? [], vendor?.id)
        : { exact: null, similar: [] },
    [name, vendors, vendor?.id, nameChanged],
  );

  const handleUseExisting = (existing: Vendor) => {
    onUseExisting?.(existing);
    onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Name is required.');
      return;
    }
    if (matches.exact) {
      toast.error(
        `“${matches.exact.name}” already exists — use that vendor instead of creating a duplicate.`,
      );
      return;
    }

    const fields = {
      name: trimmedName,
      vendor_type: vendorType === TYPE_NONE ? null : (vendorType as VendorType),
      email: email.trim() || null,
      phone: phone.trim() || null,
      website: website.trim() || null,
      contact_name: contactName.trim() || null,
      contact_owner: contactOwner.trim() || null,
      address_line1: addressLine1.trim() || null,
      address_line2: addressLine2.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      zip_code: zipCode.trim() || null,
      supplies: supplies.trim() || null,
      notes: notes.trim() || null,
    };

    try {
      if (isEdit && vendor) {
        const updated = await updateVendor.mutateAsync({ id: vendor.id, ...fields });
        toast.success(`Updated “${updated.name}”`);
        onOpenChange(false);
      } else {
        const created = await createVendor.mutateAsync(fields);
        toast.success(`Created “${created.name}”`);
        onOpenChange(false);
        onCreated?.(created.id);
      }
    } catch (err) {
      toast.error(formatError(err, `Failed to ${isEdit ? 'update' : 'create'} supplier.`));
    }
  };

  const submitting = createVendor.isPending || updateVendor.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit supplier' : 'Add supplier'}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="supplier-name">
                Name <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="supplier-name"
                autoFocus={!isEdit}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Pacific Packaging Co."
                maxLength={200}
                required
              />
            </div>

            {(matches.exact || matches.similar.length > 0) && (
              <div className="space-y-2 rounded-md border border-fresh-salmon-dark/40 bg-fresh-salmon-dark/5 px-3 py-2.5 text-sm sm:col-span-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-fresh-salmon-dark" />
                  <p className="text-foreground">
                    {matches.exact
                      ? 'This vendor already exists. Use the existing record instead of creating a duplicate.'
                      : 'Similar vendors already exist. Make sure this isn’t one of them before creating a new one.'}
                  </p>
                </div>
                <ul className="space-y-1 pl-6">
                  {(matches.exact ? [matches.exact] : matches.similar).map((v) => (
                    <li key={v.id} className="flex items-center justify-between gap-3">
                      <span className="truncate">
                        <span className="font-medium">{v.name}</span>
                        {!v.is_active && (
                          <span className="ml-1.5 text-xs text-muted-foreground">(inactive)</span>
                        )}
                      </span>
                      {onUseExisting && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 shrink-0"
                          onClick={() => handleUseExisting(v)}
                        >
                          Use this vendor
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="supplier-type">Type</Label>
              <Select value={vendorType} onValueChange={setVendorType}>
                <SelectTrigger id="supplier-type">
                  <SelectValue placeholder="Select a type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TYPE_NONE}>
                    <span className="text-muted-foreground">No type</span>
                  </SelectItem>
                  {VENDOR_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {VENDOR_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="supplier-email">Email</Label>
              <Input
                id="supplier-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. ap@pacpack.com"
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="supplier-phone">Phone</Label>
              <PhoneNumberInput
                id="supplier-phone"
                value={phone}
                onChange={setPhone}
                maxLength={50}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="supplier-contact-name">Contact name</Label>
              <Input
                id="supplier-contact-name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="e.g. Jane Rivera"
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="supplier-contact-owner">Contact owner</Label>
              <Input
                id="supplier-contact-owner"
                value={contactOwner}
                onChange={(e) => setContactOwner(e.target.value)}
                placeholder="Who owns this relationship internally?"
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="supplier-website">Website</Label>
              <Input
                id="supplier-website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="e.g. https://pacpack.com"
                maxLength={300}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="supplier-address1">Address</Label>
              <Input
                id="supplier-address1"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder="Street address"
                maxLength={300}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="supplier-address2">Address line 2</Label>
              <Input
                id="supplier-address2"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                placeholder="Suite, unit, building (optional)"
                maxLength={300}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="supplier-city">City</Label>
              <Input
                id="supplier-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Portland"
                maxLength={120}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="supplier-state">State</Label>
                <Input
                  id="supplier-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="e.g. OR"
                  maxLength={60}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="supplier-zip">Zip code</Label>
                <Input
                  id="supplier-zip"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="e.g. 97201"
                  maxLength={20}
                />
              </div>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="supplier-supplies">What it supplies</Label>
              <Textarea
                id="supplier-supplies"
                value={supplies}
                onChange={(e) => setSupplies(e.target.value)}
                placeholder="e.g. Corrugated boxes, custom inserts, void fill."
                rows={2}
                maxLength={1000}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="supplier-notes">Notes</Label>
              <Textarea
                id="supplier-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Payment terms, contacts, anything worth remembering."
                rows={3}
                maxLength={2000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !!matches.exact}>
              {submitting
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : isEdit
                  ? 'Save changes'
                  : matches.similar.length > 0
                    ? 'Create anyway'
                    : 'Create supplier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
